import { useState, useRef, useEffect } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/services/api';
import type { ManagedTableInfo, TableInfo } from '@/types/api';
import TableLayoutEditor, { type TablePosition, type LayoutOption, type PendingTableOp } from '@/components/TableLayoutEditor';
import { applyPendingTableOps, buildFinalPositions, ensureLayoutId } from './tableLayoutSaveHelpers';
import { ArrowLeft, Loader2, ImagePlus, Trash2, AlertTriangle, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useHasPermission } from '@/hooks/usePermission';
import { PERM } from '@/constants/permissions';
import { DraftModeBar } from '@/components/common/DraftModeBar';
import { useRestaurantId } from '@/hooks/useRestaurantId';
import { pendingSyncRefetchInterval } from '@/utils/pendingSyncPolling';

const MAX_IMAGE_KB = 500;

// Runs one update call per table and reports exactly which tables failed,
// instead of Promise.all's all-or-nothing rejection - a partial failure here
// still leaves the successfully-updated tables saved, so the user needs to
// know which specific ones to retry rather than "something failed."
async function saveTableUpdates(
  updates: Array<{ tableId: number; run: () => Promise<unknown> }>,
  floorTables: ManagedTableInfo[]
): Promise<void> {
  if (updates.length === 0) return;
  const results = await Promise.allSettled(updates.map((u) => u.run()));
  const failedTableIds = results
    .map((result, index) => (result.status === 'rejected' ? updates[index].tableId : null))
    .filter((id): id is number => id !== null);

  if (failedTableIds.length > 0) {
    const names = failedTableIds.map((id) => {
      const table = floorTables.find((t) => t.id === id);
      return table?.name || `Table #${id}`;
    });
    throw new Error(
      `Some changes didn't save: ${names.join(', ')}. The rest of the layout was saved - try again for just these tables.`
    );
  }
}

function resizeImageFile(file: File, targetWidth: number, targetHeight: number): Promise<File> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const canvas = document.createElement('canvas');
      canvas.width = targetWidth;
      canvas.height = targetHeight;
      // Letterbox instead of force-fitting to targetWidth×targetHeight: a
      // straight drawImage(0, 0, targetWidth, targetHeight) stretches the
      // source to the floor's aspect ratio, distorting anything that doesn't
      // already match it. Scale to fit within the box and center it (matching
      // the editor's objectFit: 'contain' display) so the stored image keeps
      // its original proportions.
      const scale = Math.min(targetWidth / img.width, targetHeight / img.height);
      const drawWidth = img.width * scale;
      const drawHeight = img.height * scale;
      const offsetX = (targetWidth - drawWidth) / 2;
      const offsetY = (targetHeight - drawHeight) / 2;
      canvas.getContext('2d')!.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
      canvas.toBlob((blob) => {
        if (!blob) { reject(new Error('Resize failed')); return; }
        resolve(new File([blob], file.name.replace(/\.[^.]+$/, '.png'), { type: 'image/png' }));
      }, 'image/png');
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Image load failed')); };
    img.src = url;
  });
}

export default function TableLayoutPage() {
  const restaurantId = useRestaurantId();
  const { floorId: floorIdParam } = useParams<{ floorId?: string }>();
  const floorId = floorIdParam ? parseInt(floorIdParam) : null;
  const queryClient = useQueryClient();

  // Every write action on this page — image upload/remove and layout save — maps to
  // floor.update on the backend (FloorManagementController); there is no floor.delete
  // endpoint here (removing the background image is a PUT-style clear, not a delete).
  const canUpdate = useHasPermission(PERM.FLOOR_UPDATE);

  const [selectedLayoutId, setSelectedLayoutId] = useState<number | null>(null);
  const [imageError, setImageError] = useState<string | null>(null);
  const [saveNotice, setSaveNotice] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [isEditorDirty, setIsEditorDirty] = useState(false);

  // Covers tab close/refresh. react-router-dom v6's useBlocker requires a data
  // router (createBrowserRouter), but this app uses plain <BrowserRouter> - so
  // in-app navigation via other routes/links (e.g. a sidebar) isn't covered
  // without a broader router migration. The "Back to Floors" link below is
  // guarded directly since it's the one in-page navigation affordance here.
  useEffect(() => {
    if (!isEditorDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isEditorDirty]);
  const imageInputRef = useRef<HTMLInputElement>(null);

  // Fetch managed tables for the floor
  const {
    data: floorTables = [],
    isLoading: tablesLoading,
    isError: tablesError,
    refetch: refetchTables,
  } = useQuery({
    queryKey: ['managed-tables', restaurantId, floorId],
    queryFn: () => apiClient.getManagedTablesByFloor(restaurantId!, floorId!),
    enabled: !!restaurantId && !!floorId,
    // Poll while a row is still pending (e.g. just pushed from draft mode), so the
    // editor stops showing "Pending Sync" without needing a manual refresh.
    refetchInterval: pendingSyncRefetchInterval(`managed-tables:${restaurantId}:${floorId}`),
  });

  // Fetch floor layouts
  const {
    data: layouts = [],
    isLoading: layoutsLoading,
    isError: layoutsError,
    refetch: refetchLayouts,
  } = useQuery({
    queryKey: ['layouts', restaurantId, floorId],
    queryFn: () => apiClient.getLayouts(restaurantId!, floorId!),
    enabled: !!restaurantId && !!floorId,
    refetchInterval: pendingSyncRefetchInterval(`layouts:${restaurantId}:${floorId}`),
  });

  // Fetch restaurant details
  const { data: restaurant, isError: restaurantError, refetch: refetchRestaurant } = useQuery({
    queryKey: ['restaurant', restaurantId],
    queryFn: () => apiClient.getRestaurant(restaurantId!),
    enabled: !!restaurantId,
  });

  // Fetch floor details when floorId is present
  const { data: floor, isError: floorError, refetch: refetchFloor } = useQuery({
    queryKey: ['floor', restaurantId, floorId],
    queryFn: () => apiClient.getFloor(restaurantId!, floorId!),
    enabled: !!restaurantId && !!floorId,
  });

  // Tables with no POS floor - offered in the canvas's "add unassigned table" popup.
  // Not part of hasQueryError/retryAllQueries below: it's a picker convenience, not core
  // layout data, so a failure here shouldn't block the whole editor from loading.
  const { data: unassignedTables = [], isLoading: unassignedLoading } = useQuery({
    queryKey: ['unassigned-tables', restaurantId],
    queryFn: () => apiClient.getUnassignedTables(restaurantId!),
    enabled: !!restaurantId && !!floorId && !!canUpdate,
  });

  const hasQueryError = tablesError || layoutsError || restaurantError || floorError;
  const retryAllQueries = () => {
    refetchTables();
    refetchLayouts();
    refetchRestaurant();
    refetchFloor();
  };

  // Resolve which floor layout is active: user selection → default → first
  const activeLayout = layouts.find(l => l.id === selectedLayoutId)
    ?? layouts.find(l => l.isDefault)
    ?? layouts[0]
    ?? null;

  // Map ManagedTableInfo → TableInfo shape for TableLayoutEditor
  const editorTables: TableInfo[] = floorTables.map((t) => ({
    id: t.id,
    restaurantId: restaurantId!,
    tableNumber: t.name || `Table ${t.id}`,
    section: t.zoneName || '',
    capacity: t.capacity,
    status: 'AVAILABLE' as TableInfo['status'],
    updatedAt: t.modifiedDate,
  }));

  // POS-synced table geometry (managed_tables x/y/width/height/shape mirrors
  // SHOP_TABLE on the POS). Used as the fallback position so a floor without a
  // layout — or a table missing from the active layout — renders like the POS
  // floor plan instead of a generic grid.
  const tableGeometryPosition = (t: ManagedTableInfo): TablePosition => ({
    tableId: t.id,
    x: t.x ?? 0,
    y: t.y ?? 0,
    rotation: 0,
    width: t.width || 120,
    height: t.height || 100,
    shape: t.shape ?? 'rectangle',
  });

  // Map LayoutPositionInfo → TablePosition for the editor (seed shape from managed table)
  const layoutPositions: TablePosition[] = (() => {
    if (activeLayout) {
      const positioned = new Set(activeLayout.positions.map(p => p.tableId));
      return [
        ...activeLayout.positions.map(p => ({
          tableId: p.tableId, x: p.x, y: p.y, rotation: 0,
          width: p.width ?? 120, height: p.height ?? 100,
          shape: floorTables.find(t => t.id === p.tableId)?.shape ?? 'rectangle',
        })),
        // Tables not in the layout fall back to their own POS geometry
        ...floorTables.filter(t => !positioned.has(t.id)).map(tableGeometryPosition),
      ];
    }
    // No layout for this floor — draw tables from their POS-synced geometry
    return floorTables.map(tableGeometryPosition);
  })();

  // Image upload mutation
  const uploadImageMutation = useMutation({
    mutationFn: (file: File) => apiClient.uploadFloorImage(restaurantId!, floorId!, file),
    onSuccess: () => {
      setImageError(null);
      queryClient.invalidateQueries({ queryKey: ['floor', restaurantId, floorId] });
      queryClient.invalidateQueries({ queryKey: ['floors', restaurantId] });
    },
    onError: (error: any) => {
      setImageError(error.response?.data?.message || 'Failed to upload background image');
    },
  });

  // Image delete mutation
  const deleteImageMutation = useMutation({
    mutationFn: () => apiClient.deleteFloorImage(restaurantId!, floorId!),
    onSuccess: () => {
      setImageError(null);
      queryClient.invalidateQueries({ queryKey: ['floor', restaurantId, floorId] });
      queryClient.invalidateQueries({ queryKey: ['floors', restaurantId] });
    },
    onError: (error: any) => {
      setImageError(error.response?.data?.message || 'Failed to remove background image');
    },
  });

  const handleImageFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    if (file.size > MAX_IMAGE_KB * 1024) {
      const targetW = floor?.width ?? 850;
      const targetH = floor?.height ?? 700;
      const confirmed = window.confirm(
        `The image is larger than ${MAX_IMAGE_KB} KB.\n\nResize it to ${targetW}×${targetH}px before uploading?`
      );
      if (!confirmed) return;
      try {
        const resized = await resizeImageFile(file, targetW, targetH);
        uploadImageMutation.mutate(resized);
      } catch {
        setImageError('Failed to resize image. Please try a smaller file.');
      }
      return;
    }

    uploadImageMutation.mutate(file);
  };

  // Save layout mutation — commits staged popup actions (delete/detach/create/assign, in that
  // order), then persists positions and any shape changes on managed tables (folding newly
  // created/assigned tables in via their real id).
  const saveLayoutMutation = useMutation({
    mutationFn: async ({ positions: rawPositions, pendingOps }: { positions: TablePosition[]; pendingOps: PendingTableOp[] }) => {
      const { tempIdToRealId } = await applyPendingTableOps(pendingOps, rawPositions, {
        restaurantId: restaurantId!,
        floorId: floorId!,
        deleteManagedTable: apiClient.deleteManagedTable.bind(apiClient),
        detachManagedTable: apiClient.detachManagedTable.bind(apiClient),
        createManagedTable: apiClient.createManagedTable.bind(apiClient),
      });
      const positions = buildFinalPositions(rawPositions, pendingOps, tempIdToRealId);

      // A floor's first save has no layout yet — auto-create a default one (named after the
      // floor, sized to match it) rather than writing geometry straight onto managed_tables;
      // every save from here on has a real layout to persist into.
      const layoutId = await ensureLayoutId(
        activeLayout, floor, floorId!, restaurantId!, apiClient.createLayout.bind(apiClient)
      );

      // 1. Save layout positions
      await apiClient.updateLayout(restaurantId!, layoutId, {
        positions: positions.map(p => ({
          tableId: p.tableId,
          x: p.x,
          y: p.y,
          width: p.width,
          height: p.height,
          hidden: false,
        })),
      });

      // 2. Persist shape changes — shape lives on ManagedTable, not on the layout position
      const shapeChanges = positions.filter(pos => {
        const original = floorTables.find(t => t.id === pos.tableId);
        return original && pos.shape && pos.shape !== original.shape;
      });
      await saveTableUpdates(
        shapeChanges.map(pos => ({
          tableId: pos.tableId,
          run: () => apiClient.updateManagedTable(restaurantId!, pos.tableId, { shape: pos.shape }),
        })),
        floorTables
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['layouts', restaurantId, floorId] });
      queryClient.invalidateQueries({ queryKey: ['managed-tables', restaurantId, floorId] });
      queryClient.invalidateQueries({ queryKey: ['unassigned-tables', restaurantId] });
      // Draft mode may have just staged (rather than sent) these changes — refresh
      // the badge on this page's DraftModeBar rather than waiting for its own poll tick.
      queryClient.invalidateQueries({ queryKey: ['draftStatus'] });
      setSaveNotice({ type: 'success', message: 'Layout saved successfully!' });
    },
    onError: (error: unknown) => {
      const message = error instanceof Error && error.message
        ? error.message
        : 'Failed to save layout. Please try again.';
      setSaveNotice({ type: 'error', message });
    },
  });

  const handleSave = (positions: TablePosition[], pendingOps: PendingTableOp[]) => {
    return saveLayoutMutation.mutateAsync({ positions, pendingOps });
  };

  if (restaurantId === null) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3 text-center">
        <AlertTriangle className="h-8 w-8 text-red-500" />
        <div className="text-gray-900 font-medium">Invalid restaurant ID</div>
      </div>
    );
  }

  // Layouts are always edited per floor — the legacy whole-restaurant editor was removed
  if (!floorId) {
    return <Navigate to={`/restaurants/${restaurantId}/tables?tab=floors`} replace />;
  }

  if (hasQueryError) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3 text-center">
        <AlertTriangle className="h-8 w-8 text-red-500" />
        <div className="text-gray-900 font-medium">Failed to load layout editor data</div>
        <button onClick={retryAllQueries} className="btn btn-outline h-10">
          <RefreshCw className="h-4 w-4 mr-2" />
          Retry
        </button>
      </div>
    );
  }

  if (tablesLoading || layoutsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
        <span className="ml-3 text-gray-500">Loading...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to={`/restaurants/${restaurantId}/tables?tab=floors`}
            className="btn btn-outline h-10"
            onClick={(e) => {
              if (isEditorDirty && !window.confirm('You have unsaved layout changes. Leave without saving?')) {
                e.preventDefault();
              }
            }}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Floors
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {floor ? `${floor.name} — Layout Editor` : 'Table Layout Editor'}
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              {restaurant?.name || 'Restaurant'} - Arrange tables on the floor plan
            </p>
          </div>
        </div>

        {floorId && canUpdate && (
          <div className="flex items-center gap-3">
            {/* Floor background image — applies to the whole floor, not a specific layout */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 whitespace-nowrap">Floor background:</span>
              <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleImageFileChange}
              />
              <button
                onClick={() => imageInputRef.current?.click()}
                disabled={uploadImageMutation.isPending}
                className="btn btn-outline h-10 text-sm"
                title={floor?.backgroundImageBase64 ? 'Replace background image' : 'Upload background image'}
              >
                {uploadImageMutation.isPending
                  ? <Loader2 className="h-4 w-4 animate-spin" />
                  : <ImagePlus className="h-4 w-4" />}
                <span className="ml-2">{floor?.backgroundImageBase64 ? 'Change Image' : 'Set Image'}</span>
              </button>
              {floor?.backgroundImageBase64 && (
                <button
                  onClick={() => deleteImageMutation.mutate()}
                  disabled={deleteImageMutation.isPending}
                  className="btn btn-outline h-10 text-sm text-red-600 hover:text-red-700 border-red-300 hover:border-red-400"
                  title="Remove background image"
                >
                  {deleteImageMutation.isPending
                    ? <Loader2 className="h-4 w-4 animate-spin" />
                    : <Trash2 className="h-4 w-4" />}
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* The layout editor writes the same floor-area entities as the Floors tab,
          so the same staged state has to be visible and actionable from here. */}
      <DraftModeBar
        restaurantId={restaurantId}
        area="FLOOR"
        permission={PERM.FLOOR_UPDATE}
      />

      {saveNotice && (
        <div
          className={`p-3 border rounded-md text-sm flex items-center justify-between ${
            saveNotice.type === 'success'
              ? 'bg-green-50 border-green-200 text-green-700'
              : 'bg-red-50 border-red-200 text-red-700'
          }`}
        >
          <span>{saveNotice.message}</span>
          <button
            onClick={() => setSaveNotice(null)}
            className={`font-medium ml-4 ${
              saveNotice.type === 'success' ? 'text-green-700 hover:text-green-900' : 'text-red-700 hover:text-red-900'
            }`}
          >
            Dismiss
          </button>
        </div>
      )}

      {imageError && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm flex items-center justify-between">
          <span>{imageError}</span>
          <button onClick={() => setImageError(null)} className="text-red-700 hover:text-red-900 font-medium ml-4">
            Dismiss
          </button>
        </div>
      )}

      {/* Instructions */}
      <div className="card p-4 bg-blue-50 border-blue-200">
        <h3 className="text-sm font-semibold text-blue-900 mb-2">
          How to use:
        </h3>
        <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
          <li>Drag tables to position them on the floor plan</li>
          <li>Click the rotate button on a table to change its orientation</li>
          <li>Click empty space to add an unassigned table or create a new one</li>
          <li>Click a table to detach it from this floor or delete it</li>
          <li>Use the "Reset Layout" button to return to grid arrangement</li>
          <li>Click "Save Layout" to persist your changes</li>
        </ul>
      </div>

      {/* No tables assigned yet — the canvas below still renders (empty, sized
          to the floor's dimensions) so this is just a hint, not a dead end. */}
      {editorTables.length === 0 && (
        <div className="card p-4 bg-yellow-50 border-yellow-200 text-sm text-yellow-800 flex items-center justify-between flex-wrap gap-3">
          <span>No tables assigned to this floor yet. Add tables in Floors &amp; Zones, then arrange them here.</span>
          <Link
            to={`/restaurants/${restaurantId}/tables?tab=floors`}
            className="btn btn-outline h-9 text-sm"
          >
            Go to Floors &amp; Zones
          </Link>
        </div>
      )}

      {/* Layout Editor — key forces remount when layout switches */}
      <TableLayoutEditor
        key={activeLayout?.id ?? 'default'}
        tables={editorTables}
        initialPositions={layoutPositions}
        onSave={handleSave}
        backgroundImage={
          floor?.backgroundImageBase64 && floor.backgroundImageContentType
            ? { base64: floor.backgroundImageBase64, contentType: floor.backgroundImageContentType }
            : null
        }
        designWidth={activeLayout?.designWidth ?? floor?.width ?? 800}
        designHeight={activeLayout?.designHeight ?? floor?.height ?? 600}
        layouts={layouts.map<LayoutOption>((l) => ({
          id: l.id,
          name: l.name,
          isDefault: l.isDefault,
          shiftName: l.shiftName ?? undefined,
        }))}
        activeLayoutId={activeLayout?.id}
        onLayoutChange={(id) => setSelectedLayoutId(id)}
        canSave={canUpdate}
        onDirtyChange={setIsEditorDirty}
        unassignedTables={unassignedTables}
        loadingUnassignedTables={unassignedLoading}
      />

      {/* Saving Indicator */}
      {saveLayoutMutation.isPending && (
        <div className="fixed bottom-4 right-4 bg-white p-4 rounded-lg shadow-lg border border-gray-200">
          <div className="flex items-center space-x-3">
            <Loader2 className="h-5 w-5 animate-spin text-primary-600" />
            <span className="text-sm text-gray-700">Saving layout...</span>
          </div>
        </div>
      )}
    </div>
  );
}
